import { lazy, type ReactNode } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Lock } from "lucide-react";
import { AppShell } from "@/components/layout/AppShell";
import { BrandMark, EmptyState, Notice } from "@/components/crm/blocks";
import { Button } from "@/components/ui/button";
import { Toaster } from "@/components/ui/sonner";
import { CrmProvider, useCrm } from "@/hooks/useCrm";
import { WorkspaceProvider } from "@/hooks/useWorkspace";
import { LoginPage } from "@/pages/auth/LoginPage";
import { PasswordPage } from "@/pages/auth/PasswordPage";

// Elk scherm is een apart bestand dat pas laadt wanneer je het opent (code splitting); aanmelden
// en wachtwoord zitten in het hoofdbestand, zodat de eerste pagina meteen staat.
const page = <K extends string>(name: K, load: () => Promise<Record<K, () => ReactNode>>) => lazy(() => load().then((m) => ({ default: m[name] })));
const AccountPage = page("AccountPage", () => import("@/pages/AccountPage"));
const ActiveRentalsPage = page("ActiveRentalsPage", () => import("@/pages/ActiveRentalsPage"));
const AuditPage = page("AuditPage", () => import("@/pages/AuditPage"));
const BoardPage = page("BoardPage", () => import("@/pages/BoardPage"));
const BoardsPage = page("BoardsPage", () => import("@/pages/BoardsPage"));
const CheckoutPage = page("CheckoutPage", () => import("@/pages/CheckoutPage"));
const CompanyPage = page("CompanyPage", () => import("@/pages/CompanyPage"));
const ContractsPage = page("ContractsPage", () => import("@/pages/ContractsPage"));
const DashboardPage = page("DashboardPage", () => import("@/pages/DashboardPage"));
const InvoicesPage = page("InvoicesPage", () => import("@/pages/InvoicesPage"));
const PlanningPage = page("PlanningPage", () => import("@/pages/PlanningPage"));
const PropertiesPage = page("PropertiesPage", () => import("@/pages/PropertiesPage"));
const PropertyPage = page("PropertyPage", () => import("@/pages/PropertyPage"));
const RelationshipsPage = page("RelationshipsPage", () => import("@/pages/RelationshipsPage"));
const RentalsPage = page("RentalsPage", () => import("@/pages/RentalsPage"));
const SettingsPage = page("SettingsPage", () => import("@/pages/SettingsPage"));
const UsersPage = page("UsersPage", () => import("@/pages/UsersPage"));

// Adressen van de app. Elk scherm heeft een eigen URL (een pand: /panden/recP01), zodat je een link
// kan delen en de terugknop van de browser werkt.

export function App() {
  return (
    <CrmProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginRoute />} />
          {/* De link in de mail is /wachtwoord#token=…; /wachtwoord.html en /login.html uit oudere mails werken nog. */}
          <Route path="/wachtwoord" element={<PasswordPage />} />
          <Route path="/wachtwoord.html" element={<PasswordPage />} />
          <Route path="/login.html" element={<Navigate to="/login" replace />} />
          <Route element={<AuthGate />}>
            <Route index element={<DashboardPage />} />
            <Route path="/account" element={<AccountPage />} />
            <Route path="/relaties" element={<RelationshipsPage />} />
            <Route path="/panden" element={<PropertiesPage />} />
            <Route path="/panden/:id" element={<PropertyPage />} />
            <Route path="/bedrijven/:id" element={<CompanyPage />} />
            <Route path="/verhuur" element={<RentalsPage />} />
            <Route path="/contracten" element={<ContractsPage />} />
            <Route path="/actieve-verhuur" element={<ActiveRentalsPage />} />
            <Route path="/uittrede" element={<CheckoutPage />} />
            <Route path="/planning" element={<PlanningPage />} />
            <Route path="/borden" element={<RequirePermission permission="borden.beheren"><BoardsPage /></RequirePermission>} />
            <Route path="/borden/:id" element={<BoardPage />} />
            <Route path="/facturen" element={<RequirePermission permission="facturen.lezen"><InvoicesPage /></RequirePermission>} />
            <Route path="/gebruikers" element={<RequirePermission permission="gebruikers.beheren"><UsersPage /></RequirePermission>} />
            <Route path="/auditlog" element={<RequirePermission permission="audit.lezen"><AuditPage /></RequirePermission>} />
            <Route path="/instellingen" element={<RequirePermission permission="platform.beheren"><SettingsPage /></RequirePermission>} />
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster />
    </CrmProvider>
  );
}

/** Alles achter het aanmelden: laden, doorsturen naar /login, 2FA-plicht, foutmelding. */
function AuthGate() {
  const crm = useCrm();
  const location = useLocation();
  if (crm.phase === "loading") return <Splash />;
  if (crm.phase === "anonymous") return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  if (crm.phase === "error") {
    return (
      <Centered>
        <Notice tone="red" title="Het CRM kon niet laden">
          {crm.error}
        </Notice>
        <Button className="mt-3" onClick={() => void crm.reload()}>
          Opnieuw proberen
        </Button>
      </Centered>
    );
  }
  if (crm.me?.mustSetup2fa && location.pathname !== "/account") return <Navigate to="/account" replace />;
  return (
    <WorkspaceProvider>
      <AppShell />
    </WorkspaceProvider>
  );
}

function LoginRoute() {
  const crm = useCrm();
  if (crm.phase === "loading") return <Splash />;
  if (crm.phase === "ready") return <Navigate to="/" replace />;
  return <LoginPage />;
}

function RequirePermission({ permission, children }: { permission: string; children: ReactNode }) {
  const crm = useCrm();
  if (!crm.can(permission)) return <EmptyState icon={Lock} title="Geen toegang" text="Je hebt geen recht op dit scherm. Vraag het aan een beheerder." />;
  return <>{children}</>;
}

function NotFound() {
  return <EmptyState title="Niet gevonden" text="Deze pagina bestaat niet." />;
}

function Splash() {
  return (
    <Centered>
      <BrandMark className="mx-auto size-12 animate-pulse text-navy-800" />
    </Centered>
  );
}

function Centered({ children }: { children: ReactNode }) {
  return <div className="grid min-h-full place-items-center p-6 text-center">{<div className="max-w-md">{children}</div>}</div>;
}
